import { getAuthToken } from '../utils/authUtils';

const API_BASE_URL = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

/**
 * Get form by ID
 * @param {string} formId - The form ID
 * @returns {Promise<Object>} Form data
 */
export const getForm = async (formId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/forms/${formId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching form:', error);
    throw error;
  }
};

/**
 * Update form assignments
 * @param {string} formId - The form ID
 * @param {Object} assignments - The assignments data
 * @returns {Promise<Object>} Update result
 */
export const updateFormAssignments = async (formId, assignments) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/forms/${formId}/assignments`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(assignments)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating form assignments:', error);
    throw error;
  }
};

/**
 * Get all forms
 * @returns {Promise<Array>} Array of all forms
 */
export const getAllForms = async () => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/forms`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching all forms:', error);
    throw error;
  }
};

/**
 * Create a new form
 * @param {Object} formData - The form data
 * @returns {Promise<Object>} Created form data
 */
export const createForm = async (formData) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/forms`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating form:', error);
    throw error;
  }
};

/**
 * Update an existing form
 * @param {string} formId - The form ID
 * @param {Object} updateData - The data to update
 * @returns {Promise<Object>} Updated form data
 */
export const updateForm = async (formId, updateData) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/forms/${formId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updateData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating form:', error);
    throw error;
  }
};

/**
 * Delete a form
 * @param {string} formId - The form ID
 * @returns {Promise<Object>} Deletion result
 */
export const deleteForm = async (formId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/forms/${formId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting form:', error);
    throw error;
  }
};

// Alias for getForm to maintain compatibility
export const getFormById = getForm;
